(() => {
  const dialog = document.querySelector('[data-quick-order]');
  if (!dialog) return;

  let ranges = [];
  let rangesPromise;
  let navigationData = { brands: [], ranges: [] };
  try {
    navigationData = JSON.parse(document.getElementById('QuickOrderNavigationData')?.textContent || '{}');
  } catch (error) {
    console.error('Quick Order could not read its navigation ranges.', error);
  }
  const brands = [...new Set((navigationData.brands || []).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right));

  const categories = [...dialog.querySelectorAll('[data-quick-order-category]')];
  const rangeBox = dialog.querySelector('[data-quick-order-ranges]');
  const strengthBox = dialog.querySelector('[data-quick-order-strengths]');
  const results = dialog.querySelector('[data-quick-order-results]');
  const search = dialog.querySelector('[data-quick-order-search]');
  const status = dialog.querySelector('[data-quick-order-status]');
  const cartLink = dialog.querySelector('[data-quick-order-cart]');
  const basketTotal = dialog.querySelector('[data-quick-order-basket-total]');
  const root = window.Shopify?.routes?.root || '/';
  let category = 'popular';
  let rangeId = '';
  let strength = '';
  let selectedBrand = '';
  let brandRange = null;
  let preferredVariantId = '';
  let preferredProductHandle = '';
  let requestId = 0;
  let previousFocus = null;
  let hadNoScroll = false;

  function ensureRanges() {
    if (rangesPromise) return rangesPromise;
    rangesPromise = (async () => {
      const url = new URL(root, location.origin);
      url.searchParams.set('view', 'quick-order-data');
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Could not load product ranges. Close this window and try again.');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = page.getElementById('QuickOrderData');
      if (!data) throw new Error('Could not load product ranges. Close this window and try again.');
      const parsed = JSON.parse(data.textContent);
      if (!Array.isArray(parsed.ranges)) throw new Error('Product range data is invalid.');
      const loaded = parsed.ranges;
      const configuredHandles = new Set(loaded.map(range => range.handle));
      (navigationData.ranges || []).forEach(range => {
        if (!configuredHandles.has(range.handle)) {
          loaded.push(range);
          configuredHandles.add(range.handle);
        }
      });
      loaded.forEach(range => {
        range.size = (range.title || '').match(/\b(?:10|50|100)\s?ml\b/i)?.[0]?.replace(/\s/g, '') || '';
      });
      ranges = loaded;
    })().catch(error => {
      rangesPromise = undefined;
      throw error;
    });
    return rangesPromise;
  }

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const categoryRanges = () => ranges.filter(range => category === 'popular' ? range.popular : range.category === category);
  const selectedRange = () => category === 'brands' ? brandRange : categoryRanges().find(range => range.id === rangeId);
  const formatMoney = cents => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency', currency: window.Shopify?.currency?.active || 'GBP'
    }).format(value);
  };

  const productVariants = (products, range) => {
    const existing = new Map((range.variants || []).map(variant => [String(variant.id), variant]));
    return products.flatMap(product => {
      const strengthIndex = (product.options || []).findIndex(option =>
        /^(strength|nicotine strength|resistance|ohm|ohms)$/i.test(option.name)
      );
      return (product.variants || []).filter(variant => {
        const twentyOnly = /hayati-pro-ultra-plus-25k-prefilled-(pods|vape-kit)|hayati-pro-max-plus-6000-prefilled-pods|hyola-ultra-30k-prefilled-kit/.test(product.handle);
        return !twentyOnly || !/\b10mg\b/i.test(variant.title);
      }).map(variant => {
        const prior = existing.get(String(variant.id));
        const image = variant.featured_image?.src || product.images?.[0]?.src || product.featured_image || product.image || null;
        const optionStrength = strengthIndex < 0 ? '' : variant[`option${strengthIndex + 1}`];
        const inferredStrength = variant.title?.match(/\b(?:0|3|5|6|10|12|18|20|30)\s?mg\b/i)?.[0] || '';
        return {
          id: variant.id,
          flavour: prior?.flavour || product.title,
          strength: prior?.strength || optionStrength || inferredStrength,
          option: variant.title,
          price: Math.round(Number(variant.price || 0) * 100),
          available: Boolean(variant.available),
          image: prior?.image || image,
          url: `${root}products/${product.handle}?variant=${variant.id}`
        };
      });
    });
  };

  async function loadRange(range) {
    if (!range || range._loaded || range._loading || !range.handle || range.category === 'brands') return;
    if (!range.url?.includes('/collections/')) return;
    range._loading = true;
    renderResults();
    try {
      const products = [];
      for (let page = 1; page <= 12; page += 1) {
        const response = await fetch(`${root}collections/${encodeURIComponent(range.handle)}/products.json?limit=250&page=${page}`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Could not load this range.');
        const batch = (await response.json()).products || [];
        products.push(...batch);
        if (batch.length < 250) break;
      }
      if (products.length) {
        range.variants = productVariants(products, range);
        range.handles = products.map(product => product.handle);
      }
      range._loaded = true;
    } catch (error) {
      range._error = error.message || 'Could not load this range.';
    } finally {
      range._loading = false;
      if (selectedRange() === range) {
        const preferred = range.variants.find(variant => String(variant.id) === preferredVariantId);
        if (preferred?.strength) strength = preferred.strength;
        renderStrengths();
        renderResults();
      }
    }
  }

  async function loadBrand(brand) {
    if (!brand) return;
    const currentRequest = ++requestId;
    const query = search.value.trim();
    brandRange = {
      id: `brand-${brand}`, label: brand, category: 'brands',
      url: `${root}collections/vendors?q=${encodeURIComponent(brand)}`,
      variants: [], _loading: true
    };
    renderStrengths();
    renderResults();
    try {
      const terms = [brand, query].filter(Boolean).join(' ');
      const params = new URLSearchParams({ q: terms, 'resources[type]': 'product', 'resources[limit]': '10' });
      const response = await fetch(`${root}search/suggest.json?${params}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not search this brand.');
      const suggestions = (await response.json()).resources?.results?.products || [];
      const matches = suggestions.filter(product => product.vendor?.toLowerCase() === brand.toLowerCase());
      const products = await Promise.all(matches.map(async product => {
        const detail = await fetch(`${root}products/${encodeURIComponent(product.handle)}.js`, { headers: { Accept: 'application/json' } });
        return detail.ok ? detail.json() : null;
      }));
      if (currentRequest !== requestId || selectedBrand !== brand || category !== 'brands') return;
      brandRange.variants = productVariants(products.filter(Boolean).map(product => ({
        ...product,
        images: (product.images || []).map(image => typeof image === 'string' ? { src: image } : image),
        variants: (product.variants || []).map(variant => ({ ...variant, price: Number(variant.price || 0) / 100 }))
      })), brandRange);
    } catch (error) {
      if (currentRequest !== requestId) return;
      brandRange._error = error.message || 'Could not search this brand.';
    } finally {
      if (currentRequest === requestId && category === 'brands') {
        brandRange._loading = false;
        renderStrengths();
        renderResults();
      }
    }
  }

  function renderRanges() {
    rangeBox.replaceChildren();
    if (category === 'brands') {
      brands.forEach(brand => {
        const button = element('button', 'quick-order__range', brand);
        button.type = 'button';
        button.dataset.brandName = brand;
        button.setAttribute('aria-pressed', String(brand === selectedBrand));
        rangeBox.append(button);
      });
      return;
    }
    const visible = categoryRanges();
    if (!visible.some(range => range.id === rangeId)) rangeId = visible[0]?.id || '';
    visible.forEach(range => {
      const button = element('button', 'quick-order__range');
      button.type = 'button';
      button.dataset.rangeId = range.id;
      button.setAttribute('aria-pressed', String(range.id === rangeId));
      if (range.image) {
        const image = element('img', 'quick-order__range-image');
        image.src = range.image;
        image.width = 30;
        image.height = 30;
        image.loading = 'lazy';
        image.alt = '';
        button.append(image);
      }
      const label = range.size && !range.label.toLowerCase().includes(range.size.toLowerCase())
        ? `${range.label} · ${range.size}` : range.label;
      button.append(element('span', '', label));
      rangeBox.append(button);
    });
    rangeBox.querySelector('[aria-pressed="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  function renderStrengths() {
    strengthBox.replaceChildren();
    const choices = [...new Set((selectedRange()?.variants || []).map(variant => variant.strength).filter(Boolean))];
    if (!choices.includes(strength)) strength = '';
    if (!choices.length) {
      strengthBox.hidden = true;
      return;
    }
    strengthBox.hidden = false;
    ['', ...choices].forEach(choice => {
      const button = element('button', 'quick-order__strength', choice || 'All');
      button.type = 'button';
      button.dataset.strength = choice;
      button.setAttribute('aria-pressed', String(choice === strength));
      strengthBox.append(button);
    });
  }

  function renderResults() {
    results.replaceChildren();
    const range = selectedRange();
    if (!range) {
      results.append(element('p', 'quick-order__empty', category === 'brands' ? 'Choose a brand to see products.' : 'Choose a product range.'));
      return;
    }
    if (range._loading && !range.variants.length) {
      results.append(element('p', 'quick-order__empty', `Loading ${range.label} products…`));
      return;
    }
    if (range._error && !range.variants.length) {
      results.append(element('p', 'quick-order__empty', range._error));
    }

    const query = search.value.trim().toLocaleLowerCase();
    const matching = range.variants.filter(variant =>
      (!strength || variant.strength === strength) &&
      `${variant.flavour} ${variant.strength} ${variant.option}`.toLocaleLowerCase().includes(query)
    );
    matching.sort((left, right) => {
      const rank = variant => (String(variant.id) === preferredVariantId ? 2 :
        preferredProductHandle && variant.url?.includes(`/products/${preferredProductHandle}?`) ? 1 : 0);
      return rank(right) - rank(left);
    });
    const groups = new Map();
    matching.forEach(variant => {
      const key = variant.flavour || range.label;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(variant);
    });
    if (!groups.size) {
      if (!range._error) results.append(element('p', 'quick-order__empty', 'No matching flavours or options.'));
    }

    groups.forEach((variants, name) => {
      let current = variants.find(variant => String(variant.id) === preferredVariantId) || variants.find(variant => variant.available) || variants[0];
      const row = element('div', 'quick-order__item');
      const image = element('img', 'quick-order__image');
      image.width = 56;
      image.height = 56;
      image.loading = 'lazy';
      image.alt = '';
      if (current.image) image.src = current.image;
      else image.classList.add('quick-order__image--empty');
      const info = element('div', 'quick-order__item-info');
      const link = element('a', 'quick-order__item-name', name);
      link.href = current.url || range.url;
      const price = element('span', 'quick-order__price', formatMoney(current.price));
      info.append(link, price);
      if (range.category === 'e-liquids' && range.size) {
        info.append(element('span', 'quick-order__size', `${range.size} bottle`));
      }

      const optionChoices = [...new Set(variants.map(variant => variant.strength || variant.option))];
      let select;
      if (optionChoices.length > 1) {
        select = element('select', 'quick-order__variant-select');
        select.setAttribute('aria-label', `Choose option for ${name}`);
        variants.forEach(variant => {
          const option = element('option', '', variant.strength || variant.option);
          option.value = String(variant.id);
          option.disabled = !variant.available;
          option.selected = variant.id === current.id;
          select.append(option);
        });
        info.append(select);
      } else if (optionChoices[0] && optionChoices[0] !== 'Default Title') {
        info.append(element('span', 'quick-order__option', optionChoices[0]));
      }

      const add = element('button', 'quick-order__add', '+');
      add.type = 'button';
      add.setAttribute('aria-label', `Add ${name} to basket`);
      const quantity = element('input', 'quick-order__quantity');
      quantity.type = 'number';
      quantity.min = '1';
      quantity.max = '50';
      quantity.step = '1';
      quantity.value = '1';
      quantity.setAttribute('aria-label', `Quantity for ${name}`);
      add.disabled = !current.available;
      if (!current.available) add.title = 'Sold out';
      if (select) select.addEventListener('change', () => {
        current = variants.find(variant => String(variant.id) === select.value) || current;
        price.textContent = formatMoney(current.price);
        link.href = current.url || range.url;
        if (current.image) image.src = current.image;
        add.disabled = !current.available;
        add.title = current.available ? '' : 'Sold out';
      });
      add.addEventListener('click', () => addToCart(current, add, name, quantity));
      row.append(image, info, quantity, add);
      results.append(row);
    });
    if (range.url) {
      const browse = element('a', 'quick-order__browse-link', category === 'brands' ? `Browse all ${range.label} products` : `View the full ${range.label} range`);
      browse.href = range.url;
      results.append(browse);
    }
  }

  function render() {
    categories.forEach(button => button.setAttribute('aria-selected', String(button.dataset.quickOrderCategory === category)));
    categories.find(button => button.dataset.quickOrderCategory === category)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    renderRanges();
    renderStrengths();
    renderResults();
  }

  async function open(trigger) {
    if (!dialog.hidden) return;
    previousFocus = trigger;
    hadNoScroll = document.body.classList.contains('no-scroll');
    dialog.hidden = false;
    document.body.classList.add('no-scroll');
    dialog.setAttribute('aria-busy', 'true');
    categories.forEach(button => { button.disabled = true; });
    search.disabled = true;
    results.replaceChildren(element('p', 'quick-order__empty', 'Loading product ranges...'));
    dialog.querySelector('[data-quick-order-close]')?.focus();
    try {
      await ensureRanges();
    } catch (error) {
      results.replaceChildren(element('p', 'quick-order__empty', error.message));
      return;
    } finally {
      dialog.removeAttribute('aria-busy');
      categories.forEach(button => { button.disabled = false; });
      search.disabled = false;
    }
    if (dialog.hidden) return;
    requestId += 1;
    selectedBrand = '';
    brandRange = null;
    preferredVariantId = '';
    preferredProductHandle = '';
    if (trigger.dataset.quickOrderCategory) {
      category = trigger.dataset.quickOrderCategory || 'popular';
      rangeId = '';
    } else {
      const explicitCollection = trigger.dataset.quickOrderCollection;
      const pageCollection = dialog.dataset.quickOrderPageCollection;
      const productHandle = trigger.dataset.quickOrderProduct || dialog.dataset.quickOrderPageProduct;
      const productCollections = navigationData.productCollections || [];
      const productRange = ranges.filter(item => productCollections.includes(item.handle))
        .sort((left, right) => right.handle.length - left.handle.length)[0];
      let range = ranges.find(item => item.handle === explicitCollection);
      if (!range && explicitCollection) {
        const inferredCategory = /shortfill/i.test(explicitCollection) ? 'shortfills' :
          /nic-salt/i.test(explicitCollection) ? 'nic-salts' :
          /freebase|50-50|nicohit/i.test(explicitCollection) ? 'freebase-liquids' :
          /prefilled.*(?:kit|device)/i.test(explicitCollection) ? 'prefilled-kits' :
          /prefilled|refill/i.test(explicitCollection) ? 'prefilled-pods' : 'popular';
        range = {
          id: `context-${explicitCollection}`, handle: explicitCollection,
          title: explicitCollection.replaceAll('-', ' '), label: explicitCollection.replaceAll('-', ' '),
          category: inferredCategory, popular: inferredCategory === 'popular',
          url: `${root}collections/${encodeURIComponent(explicitCollection)}`,
          variants: [], handles: []
        };
        ranges.push(range);
      }
      range ||= productRange || ranges.find(item => item.handle === pageCollection) ||
        ranges.find(item => item.handle === productHandle || (item.handles || []).includes(productHandle));
      const pageVendor = dialog.dataset.quickOrderPageVendor;
      const matchingBrand = brands.find(brand => brand.toLowerCase() === pageVendor?.toLowerCase());
      category = range?.category || (matchingBrand ? 'brands' : 'popular');
      rangeId = range?.id || '';
      selectedBrand = !range ? matchingBrand || '' : '';
      preferredProductHandle = productHandle || '';
      if (productHandle) {
        preferredVariantId = new URLSearchParams(location.search).get('variant') ||
          document.querySelector('main form[action*="/cart/add"] [name="id"]')?.value || '';
      }
    }
    strength = '';
    search.value = '';
    status.textContent = '';
    dialog.hidden = false;
    document.body.classList.add('no-scroll');
    render();
    if (category === 'brands' && selectedBrand) loadBrand(selectedBrand);
    else {
      const range = selectedRange();
      const preferred = range?.variants.find(variant => String(variant.id) === preferredVariantId);
      if (preferred?.strength) {
        strength = preferred.strength;
        renderStrengths();
        renderResults();
      }
      loadRange(range);
    }
    search.focus();
  }

  function close() {
    dialog.hidden = true;
    if (!hadNoScroll) document.body.classList.remove('no-scroll');
    previousFocus?.focus();
  }

  async function addToCart(variant, button, name, quantityInput) {
    if (!variant.available) return;
    const quantity = Number(quantityInput.value);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 50) {
      status.textContent = 'Choose a quantity from 1 to 50.';
      quantityInput.focus();
      return;
    }
    button.disabled = true;
    status.textContent = `Adding ${quantity} × ${name}...`;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity }], sections: ['ajax-cart-drawer'] })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Could not add this item.');
      const html = result.sections?.['ajax-cart-drawer'];
      const drawerBody = document.querySelector('[data-cart-drwaer-body]');
      if (html && drawerBody) {
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (section) {
          drawerBody.innerHTML = section.innerHTML;
          if (typeof getQuantityElement === 'function') getQuantityElement(drawerBody);
          if (typeof getCartItemRemoveElements === 'function') getCartItemRemoveElements(document.querySelector('#ajax-cart-drawer'));
          if (typeof cartGiftWrapElement === 'function') cartGiftWrapElement(document.querySelector('#ajax-cart-drawer'));
        }
      }
      const cart = await fetch(`${root}cart.js`).then(response => response.json());
      document.querySelectorAll('[data-header-cart-count]').forEach(count => {
        count.textContent = cart.item_count > 99 ? '' : String(cart.item_count);
        count.classList.toggle('hidden', cart.item_count === 0);
      });
      const cartWrapper = document.querySelector('[data-cart-wrapper]');
      if (cartWrapper) cartWrapper.dataset.itemCount = cart.item_count;
      cartLink.textContent = `View basket (${cart.item_count})`;
      basketTotal.textContent = `Basket ${formatMoney(cart.total_price)}`;
      status.textContent = `${quantity} × ${name} added. Discounts are shown in your basket.`;
    } catch (error) {
      status.textContent = error.message || 'Could not add this item. Please try again.';
    } finally {
      button.disabled = false;
    }
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-quick-order-open]');
    if (trigger) {
      event.preventDefault();
      open(trigger);
    }
  });
  dialog.querySelectorAll('[data-quick-order-close]').forEach(button => button.addEventListener('click', close));
  categories.forEach(button => button.addEventListener('click', () => {
    category = button.dataset.quickOrderCategory;
    rangeId = '';
    strength = '';
    selectedBrand = '';
    brandRange = null;
    preferredVariantId = '';
    preferredProductHandle = '';
    search.value = '';
    render();
    if (category !== 'brands') loadRange(selectedRange());
  }));
  rangeBox.addEventListener('click', event => {
    const brandButton = event.target.closest('[data-brand-name]');
    if (brandButton) {
      selectedBrand = brandButton.dataset.brandName;
      strength = '';
      search.value = '';
      renderRanges();
      loadBrand(selectedBrand);
      return;
    }
    const button = event.target.closest('[data-range-id]');
    if (!button) return;
    rangeId = button.dataset.rangeId;
    strength = '';
    render();
    loadRange(selectedRange());
  });
  strengthBox.addEventListener('click', event => {
    const button = event.target.closest('[data-strength]');
    if (!button) return;
    strength = button.dataset.strength;
    renderStrengths();
    renderResults();
  });
  let brandSearchTimer;
  search.addEventListener('input', () => {
    if (category === 'brands' && selectedBrand) {
      clearTimeout(brandSearchTimer);
      brandSearchTimer = setTimeout(() => loadBrand(selectedBrand), 250);
    } else renderResults();
  });
  dialog.addEventListener('keydown', event => {
    if (event.key === 'Escape') close();
    if (event.key !== 'Tab') return;
    const focusable = [...dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled])')]
      .filter(node => node.getClientRects().length);
    if (!focusable.length) return;
    if (event.shiftKey && document.activeElement === focusable[0]) {
      event.preventDefault();
      focusable.at(-1).focus();
    } else if (!event.shiftKey && document.activeElement === focusable.at(-1)) {
      event.preventDefault();
      focusable[0].focus();
    }
  });
})();

